import { useMemo, useState, useEffect } from "react";
import { TYPE_COLORS, fmt, initials, avatarPalette } from "./data";
import { dbFetchSubscribers, dbFetchActivity } from "./supabase";

// ── Mini bar chart ─────────────────────────────────────────────────────────────
function BarChart({ data, color }) {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 80 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          <div style={{ fontSize: 10, color: "#A8A29E" }}>{d.value > 0 ? fmt(d.value).replace("₹","") : ""}</div>
          <div style={{ width: "100%", background: color || "#1C1917", borderRadius: "3px 3px 0 0", height: Math.max(4, (d.value / max) * 56), opacity: i === data.length - 1 ? 1 : 0.3 + (i / data.length) * 0.55 }} />
          <div style={{ fontSize: 10, color: "#A8A29E", whiteSpace: "nowrap" }}>{d.label}</div>
        </div>
      ))}
    </div>
  );
}

// ── Donut chart ────────────────────────────────────────────────────────────────
function DonutChart({ slices, size = 100 }) {
  const total = slices.reduce((s, x) => s + x.value, 0);
  let cumulative = 0;
  const r = 36, cx = size / 2, cy = size / 2;
  const circumference = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {slices.map((slice, i) => {
        const pct = slice.value / total;
        const rotation = cumulative * 360 - 90;
        cumulative += pct;
        return (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={slice.color} strokeWidth={14}
            strokeDasharray={`${circumference * pct} ${circumference * (1 - pct)}`}
            transform={`rotate(${rotation} ${cx} ${cy})`} />
        );
      })}
      <text x={cx} y={cy - 4} textAnchor="middle" fontSize={14} fontWeight={600} fill="#1C1917">{total}</text>
      <text x={cx} y={cy + 12} textAnchor="middle" fontSize={9} fill="#A8A29E">total</text>
    </svg>
  );
}

// ── Relative time helper ───────────────────────────────────────────────────────
const relativeTime = (isoStr) => {
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 2)   return "Just now";
  if (mins < 60)  return `${mins} min ago`;
  if (hours < 24) return `${hours} hr ago`;
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
};

export default function Dashboard({ onNavigate, userId = "default" }) {
  const [subs,     setSubs]     = useState([]);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    dbFetchSubscribers(userId).then(setSubs).catch(() => setSubs([]));
    dbFetchActivity(userId).then(setActivity).catch(() => setActivity([]));
  }, [userId]);

  const stats = useMemo(() => {
    const active  = subs.filter(s => s.status === "Active");
    const overdue = subs.filter(s => s.status === "Overdue");
    const revenue = active.reduce((a, s) => a + (s.amount || 0), 0);
    const weekly  = active.filter(s => s.plan === "Weekly").length;
    const monthly = active.filter(s => s.plan === "Monthly").length;

    // Build last-6-month revenue history from actual subscriber start dates
    const now = new Date();
    const months = Array.from({ length: 5 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (4 - i), 1);
      return { label: d.toLocaleString("default", { month: "short" }), year: d.getFullYear(), month: d.getMonth() };
    });
    const revenueHistory = months.map(m => {
      const val = subs.filter(s => {
        if (!s.startDate) return false;
        const sd = new Date(s.startDate);
        return sd.getFullYear() === m.year && sd.getMonth() === m.month && s.status === "Active";
      }).reduce((a, s) => a + (s.amount || 0), 0);
      return { label: m.label, value: val || 0 };
    });
    // Last bar is current month's active revenue
    revenueHistory[revenueHistory.length - 1].value = revenue;

    const subGrowth = months.map(m => ({
      label: m.label,
      value: subs.filter(s => {
        if (!s.startDate) return false;
        const sd = new Date(s.startDate);
        return sd.getFullYear() <= m.year && (sd.getFullYear() < m.year || sd.getMonth() <= m.month);
      }).length,
    }));

    const areaMap = {};
    active.forEach(s => { areaMap[s.deliveryArea] = (areaMap[s.deliveryArea] || 0) + 1; });
    const areas = Object.entries(areaMap).sort((a, b) => b[1] - a[1]);

    const typeMap = {};
    subs.forEach(s => { typeMap[s.type] = (typeMap[s.type] || 0) + 1; });
    const typeSlices = Object.entries(typeMap).map(([type, count]) => ({ label: type, value: count, color: TYPE_COLORS[type] }));

    // Upcoming renewals: subscribers whose end date is within next 7 days
    const today  = new Date(); today.setHours(0,0,0,0);
    const in7    = new Date(today); in7.setDate(in7.getDate() + 7);
    const todayStr = today.toISOString().slice(0,10);
    const in7Str   = in7.toISOString().slice(0,10);
    const renewals = subs.filter(s => s.endDate && s.endDate >= todayStr && s.endDate <= in7Str && s.status === "Active")
      .sort((a, b) => a.endDate.localeCompare(b.endDate));

    // Auto-generate activity from subscriber data
    const autoActivity = [];
    subs.forEach(s => {
      if (s.status === "Overdue")
        autoActivity.push({ icon: "◎", color: "#D85A30", text: `${s.name}'s payment is overdue — ${fmt(s.amount)}`, time: s.startDate });
      if (s.status === "Paused" && (s.pausePeriods||[]).length > 0) {
        const last = s.pausePeriods[s.pausePeriods.length - 1];
        autoActivity.push({ icon: "⏸", color: "#0C447C", text: `${s.name} paused deliveries (${last.from} → ${last.to})`, time: last.from });
      }
      if (s.status === "Active")
        autoActivity.push({ icon: "◎", color: "#1D9E75", text: `${s.name} is on ${s.plan} ${s.type} plan — ${fmt(s.amount)}`, time: s.startDate });
    });
    // Merge with logged activity events and sort newest first
    const allActivity = [...activity, ...autoActivity]
      .filter(a => a.time)
      .sort((a, b) => new Date(b.time) - new Date(a.time))
      .slice(0, 8);

    return { active, overdue, revenue, weekly, monthly, revenueHistory, subGrowth, areas, typeSlices, renewals, allActivity };
  }, [subs, activity]);

  const card = (children, extra = {}) => (
    <div style={{ background: "#FFF", borderRadius: 12, border: "0.5px solid #E7E5E0", padding: "1.25rem", ...extra }}>
      {children}
    </div>
  );
  const cardTitle = (title, action, actionFn) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
      <div style={{ fontSize: 12, fontWeight: 500, color: "#78716C", textTransform: "uppercase", letterSpacing: "0.04em" }}>{title}</div>
      {action && <button onClick={actionFn} style={{ fontSize: 12, color: "#1C1917", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>{action}</button>}
    </div>
  );

  return (
    <div className="page-wrap" style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", background: "#F7F6F2", minHeight: "100vh" }}>

      {/* Header */}
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.5rem" }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em", margin: 0 }}>Dashboard</h1>
          <p style={{ fontSize: 13, color: "#78716C", margin: "4px 0 0" }}>{new Date().toLocaleDateString("en-IN", { weekday:"long", day:"numeric", month:"long", year:"numeric" })}</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => onNavigate("subscribers")} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", cursor: "pointer", color: "#44403C" }}>View subscribers</button>
          <button onClick={() => onNavigate("deliveries")}  style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8, border: "none", background: "#1C1917", color: "#FFF", cursor: "pointer" }}>Today's deliveries</button>
        </div>
      </div>

      {/* Stat strip */}
      <div className="stat-grid-4" style={{ marginBottom: "1.25rem" }}>
        {[
          { label: "Monthly revenue",    value: fmt(stats.revenue),          sub: "Active subscribers",           warn: false },
          { label: "Active subscribers", value: stats.active.length,         sub: `${stats.weekly} weekly · ${stats.monthly} monthly`, warn: false },
          { label: "Overdue payments",   value: stats.overdue.length,        sub: "Need follow-up today",         warn: stats.overdue.length > 0 },
          { label: "Total subscribers",  value: subs.length,                 sub: "All plans & statuses",         warn: false },
        ].map(c => (
          <div key={c.label} style={{ background: c.warn ? "#FCEBEB" : "#FFF", border: `0.5px solid ${c.warn ? "#F09595" : "#E7E5E0"}`, borderRadius: 12, padding: "1rem 1.25rem" }}>
            <div style={{ fontSize: 12, color: c.warn ? "#A32D2D" : "#78716C", marginBottom: 6 }}>{c.label}</div>
            <div style={{ fontSize: 26, fontWeight: 600, color: c.warn ? "#A32D2D" : "#1C1917", lineHeight: 1 }}>{c.value}</div>
            <div style={{ fontSize: 11, color: c.warn ? "#C45C5C" : "#A8A29E", marginTop: 6 }}>{c.sub}</div>
          </div>
        ))}
      </div>

      {/* Row 2: Revenue + Subscriber growth */}
      <div className="two-col-chart" style={{ marginBottom: "1.25rem" }}>
        {card(<>{cardTitle("Revenue trend (₹)")} <BarChart data={stats.revenueHistory} color="#1C1917" /></>)}
        {card(<>{cardTitle("Subscriber growth")} <BarChart data={stats.subGrowth} color="#534AB7" /></>)}
      </div>

      {/* Row 3: Type donut + Area breakdown + Renewals */}
      <div className="three-col-card" style={{ marginBottom: "1.25rem" }}>

        {card(<>
          {cardTitle("Meal type split")}
          {stats.typeSlices.length === 0
            ? <div style={{ fontSize:13, color:"#A8A29E", textAlign:"center", padding:"1.5rem 0" }}>No subscribers yet</div>
            : <div style={{ display:"flex", alignItems:"center", gap:16 }}>
                <DonutChart slices={stats.typeSlices} size={110} />
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {stats.typeSlices.map(s => (
                    <div key={s.label} style={{ display:"flex", alignItems:"center", gap:7, fontSize:12 }}>
                      <div style={{ width:8, height:8, borderRadius:"50%", background:s.color, flexShrink:0 }} />
                      <span style={{ color:"#44403C" }}>{s.label}</span>
                      <span style={{ color:"#A8A29E", marginLeft:"auto" }}>{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
          }
        </>)}

        {card(<>
          {cardTitle("Active subscribers by area")}
          {stats.areas.length === 0
            ? <div style={{ fontSize:13, color:"#A8A29E", textAlign:"center", padding:"1rem 0" }}>No active subscribers</div>
            : <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {stats.areas.slice(0,6).map(([area, count]) => {
                  const pct = Math.round((count / stats.active.length) * 100);
                  return (
                    <div key={area}>
                      <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:4 }}>
                        <span style={{ color:"#44403C" }}>{area}</span>
                        <span style={{ color:"#78716C" }}>{count} · {pct}%</span>
                      </div>
                      <div style={{ height:5, background:"#F3F2EE", borderRadius:3, overflow:"hidden" }}>
                        <div style={{ height:"100%", width:`${pct}%`, background:"#1C1917", borderRadius:3 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
          }
        </>)}

        {card(<>
          {cardTitle("Renewals this week", "All subscribers →", () => onNavigate("subscribers"))}
          {stats.renewals.length === 0
            ? <div style={{ fontSize:13, color:"#A8A29E", textAlign:"center", padding:"1.5rem 0" }}>No renewals in next 7 days</div>
            : <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {stats.renewals.map(s => {
                  const av       = avatarPalette(s.id);
                  const today    = new Date(); today.setHours(0,0,0,0);
                  const endD     = new Date(s.endDate);
                  const daysLeft = Math.round((endD - today) / 86400000);
                  return (
                    <div key={s.id} style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <div style={{ width:30, height:30, borderRadius:"50%", background:av.bg, color:av.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:500, flexShrink:0 }}>
                        {initials(s.name)}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:500, color:"#1C1917" }}>{s.name}</div>
                        <div style={{ fontSize:11, color:"#A8A29E" }}>{s.plan} · {fmt(s.amount)}</div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontSize:11, fontWeight:500, color: daysLeft <= 2 ? "#A32D2D" : "#27500A" }}>
                          {daysLeft === 0 ? "Today" : daysLeft === 1 ? "Tomorrow" : `In ${daysLeft} days`}
                        </div>
                        <div style={{ fontSize:11, color:"#A8A29E" }}>{s.endDate}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
          }
        </>)}
      </div>

      {/* Row 4: Recent activity — live from this provider's data */}
      {card(<>
        {cardTitle("Recent activity")}
        {stats.allActivity.length === 0
          ? <div style={{ fontSize:13, color:"#A8A29E", textAlign:"center", padding:"2rem 0" }}>No activity yet — add subscribers to get started</div>
          : <div style={{ display:"flex", flexDirection:"column" }}>
              {stats.allActivity.map((a, i) => (
                <div key={i} style={{ display:"flex", gap:12, padding:"10px 0", borderBottom: i < stats.allActivity.length-1 ? "0.5px solid #F3F2EE" : "none" }}>
                  <div style={{ width:28, height:28, borderRadius:"50%", background:"#F7F6F2", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, color:a.color, flexShrink:0 }}>{a.icon}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, color:"#1C1917" }}>{a.text}</div>
                  </div>
                  <div style={{ fontSize:11, color:"#A8A29E", whiteSpace:"nowrap" }}>
                    {relativeTime(a.time)}
                  </div>
                </div>
              ))}
            </div>
        }
      </>)}
    </div>
  );
}
